'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { DbSprint } from '@/lib/types/db-sprint'
import { statusToString } from '@/lib/types/db-sprint'
import { Edit, Target, Trash2, Clock, PlayCircle, CheckCircle2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface EditSprintSidebarProps {
  isOpen: boolean
  onClose: () => void
  sprint: DbSprint | null
  projectId: number | null
  onSuccess?: () => void
  onDelete?: () => void
}

export function EditSprintSidebar({
  isOpen,
  onClose,
  sprint,
  projectId,
  onSuccess,
  onDelete
}: EditSprintSidebarProps) {
  const t = useTranslations('sprintsPage')
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [formData, setFormData] = useState<{
    name: string
    startDate: string
    endDate: string
    status: 'open' | 'active' | 'closed'
    capacity: string
  }>({
    name: '',
    startDate: '',
    endDate: '',
    status: 'open',
    capacity: ''
  })

  useEffect(() => {
    if (sprint) {
      setFormData({
        name: sprint.name,
        startDate: sprint.startDate ? sprint.startDate.split('T')[0] : '',
        endDate: sprint.endDate ? sprint.endDate.split('T')[0] : '',
        status: statusToString(sprint.status),
        capacity: ''
      })
    }
  }, [sprint])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!projectId || !sprint) {
      toast({
        title: 'Error',
        description: 'Missing project or sprint information',
        variant: 'destructive'
      })
      return
    }

    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      })
      return
    }

    const startDate = new Date(formData.startDate)
    const endDate = new Date(formData.endDate)

    if (startDate >= endDate) {
      toast({
        title: 'Error',
        description: 'Start date must be before end date',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)

    try {
      const statusMap: Record<string, number> = { open: 0, active: 1, closed: 2 }

      const res = await fetch(`/api/db/sprints/${sprint.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          status: statusMap[formData.status],
          startDate: formData.startDate,
          endDate: formData.endDate
        })
      })

      if (!res.ok) throw new Error('Failed to update sprint')

      toast({
        title: 'Success',
        description: 'Sprint updated successfully'
      })
      handleClose()
      onSuccess?.()
    } catch (error) {
      console.error('Error updating sprint:', error)
      toast({
        title: 'Error',
        description: 'Failed to update sprint',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!projectId || !sprint) return

    setShowDeleteConfirm(false)
    setDeleting(true)

    try {
      const res = await fetch(`/api/db/sprints/${sprint.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete sprint')

      toast({
        title: 'Success',
        description: 'Sprint deleted successfully'
      })

      // Notify sidebar to refetch sprints
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sprint-changed'))
      }

      handleClose()
      onDelete?.()
    } catch (error) {
      console.error('Error deleting sprint:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete sprint',
        variant: 'destructive'
      })
    } finally {
      setDeleting(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  if (!sprint) return null

  return (
    <>
    <GlobalSidebar
      isOpen={isOpen}
      onClose={handleClose}
      title={t('editSprint')}
      subtitle={sprint.name}
      icon={<Edit className="h-5 w-5" />}
      width="lg"
      footer={
        <div className="flex justify-between items-center px-6 py-4 border-t">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading || deleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {deleting ? 'Deleting...' : 'Delete Sprint'}
          </Button>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading || deleting}>
              Cancel
            </Button>
            <Button type="submit" form="edit-sprint-form" disabled={loading || deleting}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      }
    >
      <form id="edit-sprint-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Sprint Name */}
        <div className="space-y-2">
          <Label htmlFor="sprint-name">
            Sprint Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sprint-name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Sprint 15"
            required
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start-date">
              Start Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="start-date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-date">
              End Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="end-date"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Status and Capacity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value as 'open' | 'active' | 'closed' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span>Open</span>
                  </div>
                </SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-green-500" />
                    <span>Active</span>
                  </div>
                </SelectItem>
                <SelectItem value="closed">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-blue-500" />
                    <span>Closed</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacity (Story Points)</Label>
            <Input
              id="capacity"
              type="number"
              min="0"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              placeholder="e.g., 40"
            />
          </div>
        </div>
      </form>
    </GlobalSidebar>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sprint</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{sprint?.name}&quot;? This will also remove all issue associations with this sprint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
